'use client';

import { useEffect } from 'react';

interface UpdateHashOnScrollProps {
  sectionIds: string[];
  offsetPx?: number;
}

export default function UpdateHashOnScroll({ sectionIds, offsetPx = 120 }: UpdateHashOnScrollProps) {
  useEffect(() => {
    if (sectionIds.length === 0) return;

    const sections = sectionIds
      .map((id) => document.getElementById(id))
      .filter((section): section is HTMLElement => section !== null);

    if (sections.length === 0) return;

    let rafId = 0;

    const replaceHash = (activeId: string) => {
      const nextHash = activeId ? `#${activeId}` : '';
      if (window.location.hash === nextHash) return;
      const nextUrl = `${window.location.pathname}${window.location.search}${nextHash}`;
      window.history.replaceState(window.history.state, '', nextUrl);
    };

    const updateActiveHash = () => {
      const scrollPosition = window.scrollY + offsetPx;
      let activeId = '';

      for (const section of sections) {
        if (section.offsetTop <= scrollPosition) {
          activeId = section.id;
        } else {
          break;
        }
      }

      replaceHash(activeId);
    };

    const onScroll = () => {
      if (rafId !== 0) return;
      rafId = window.requestAnimationFrame(() => {
        rafId = 0;
        updateActiveHash();
      });
    };

    updateActiveHash();
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onScroll);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onScroll);
      if (rafId !== 0) {
        window.cancelAnimationFrame(rafId);
      }
    };
  }, [offsetPx, sectionIds]);

  return null;
}
